import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../api';
import { Post, CreatePostRequest } from '../types';
import PostEditor from './PostEditor';

// ─── PostFormModal ─────────────────────────────────────────────────────────

interface PostFormModalProps {
  post: Post | null;
  onClose: () => void;
  onSaved: (post: Post) => void;
}

const EMPTY_FORM: CreatePostRequest = {
  title: '',
  content: '',
  excerpt: '',
  coverImageUrl: '',
  authorName: '',
  tags: '',
  isPublished: true,
};

const PostFormModal: React.FC<PostFormModalProps> = ({ post, onClose, onSaved }) => {
  const [form, setForm] = useState<CreatePostRequest>(
    post
      ? {
          title: post.title,
          content: post.content,
          excerpt: post.excerpt,
          coverImageUrl: post.coverImageUrl,
          authorName: post.authorName,
          tags: post.tags,
          isPublished: post.isPublished,
        }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleContentChange = useCallback((html: string) => {
    setForm(f => ({ ...f, content: html }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let saved: Post;
      if (post) {
        saved = await adminAPI.updatePost(post.id, form);
      } else {
        saved = await adminAPI.createPost(form);
      }
      onSaved(saved);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 overflow-y-auto py-6">
      <div className="bg-slate-800 rounded-xl w-full max-w-4xl mx-4 border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">
            {post ? 'Edit Post' : 'Create New Post'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              placeholder="Post title"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Excerpt <span className="text-slate-500 font-normal">(short summary shown in feed)</span>
            </label>
            <textarea
              value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
              rows={2}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
              placeholder="A short description of the post"
            />
          </div>

          {/* Cover Image URL */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Cover Image URL <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              type="url"
              value={form.coverImageUrl}
              onChange={e => setForm(f => ({ ...f, coverImageUrl: e.target.value }))}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              placeholder="https://..."
            />
            {form.coverImageUrl && (
              <img
                src={form.coverImageUrl}
                alt="Cover preview"
                className="mt-2 h-32 object-cover rounded-lg border border-slate-600"
              />
            )}
          </div>

          {/* Author */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Author Name</label>
              <input
                type="text"
                value={form.authorName}
                onChange={e => setForm(f => ({ ...f, authorName: e.target.value }))}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                placeholder="Author name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Tags <span className="text-slate-500 font-normal">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={form.tags}
                onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                placeholder="AI, Finance, News"
              />
            </div>
          </div>

          {/* Rich content editor */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Content</label>
            <PostEditor content={form.content} onChange={handleContentChange} />
          </div>

          {/* Publish toggle & actions */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-700">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                onClick={() => setForm(f => ({ ...f, isPublished: !f.isPublished }))}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  form.isPublished ? 'bg-green-500' : 'bg-slate-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    form.isPublished ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </div>
              <div>
                <span className={`text-sm font-semibold ${
                  form.isPublished ? 'text-green-400' : 'text-slate-400'
                }`}>
                  {form.isPublished ? '✓ Published — visible on website' : '✗ Draft — not visible on website'}
                </span>
              </div>
            </label>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : post ? 'Save Changes' : 'Create Post'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── PostManagement ────────────────────────────────────────────────────────

const PostManagement: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<Post | null | undefined>(undefined); // undefined = modal closed
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getPosts();
      setPosts(data);
    } catch (err) {
      console.error('Failed to load posts', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handleSaved = (saved: Post) => {
    setPosts(prev => {
      const exists = prev.find(p => p.id === saved.id);
      return exists
        ? prev.map(p => (p.id === saved.id ? saved : p))
        : [saved, ...prev];
    });
    setEditingPost(undefined);
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(true);
    try {
      await adminAPI.deletePost(id);
      setPosts(prev => prev.filter(p => p.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Failed to delete post', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const tagList = (tags: string) =>
    tags
      ? tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Posts / Feed</h2>
          <p className="text-slate-400 text-sm mt-0.5">Manage articles shown on the public landing page</p>
        </div>
        <button
          onClick={() => setEditingPost(null)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Post
        </button>
      </div>

      {/* Posts list */}
      {loading ? (
        <div className="text-slate-400 text-center py-16">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/50 rounded-xl border border-slate-700">
          <div className="w-16 h-16 bg-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-slate-400 text-lg">No posts yet</p>
          <p className="text-slate-500 text-sm mt-1">Create your first post to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div
              key={post.id}
              className="flex items-start gap-4 bg-slate-800 rounded-xl border border-slate-700 p-4 hover:border-slate-600 transition-colors"
            >
              {/* Cover thumbnail */}
              {post.coverImageUrl ? (
                <img
                  src={post.coverImageUrl}
                  alt={post.title}
                  className="w-20 h-14 object-cover rounded-lg border border-slate-600 flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-14 bg-slate-700 rounded-lg border border-slate-600 flex-shrink-0 flex items-center justify-center">
                  <svg className="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold truncate">{post.title}</h3>
                  <span
                    className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                      post.isPublished
                        ? 'bg-green-900/50 text-green-400 border border-green-700/50'
                        : 'bg-slate-700 text-slate-400 border border-slate-600'
                    }`}
                  >
                    {post.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                {post.excerpt && (
                  <p className="text-slate-400 text-sm line-clamp-1 mb-1">{post.excerpt}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  {post.authorName && <span>by {post.authorName}</span>}
                  <span>·</span>
                  <span>{formatDate(post.createdAt)}</span>
                  {tagList(post.tags).slice(0, 3).map(tag => (
                    <span key={tag} className="bg-slate-700 text-slate-400 px-2 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditingPost(post)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => setDeleteConfirmId(post.id)}
                  className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {editingPost !== undefined && (
        <PostFormModal
          post={editingPost}
          onClose={() => setEditingPost(undefined)}
          onSaved={handleSaved}
        />
      )}

      {/* Delete confirm dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-2">Delete Post</h3>
            <p className="text-slate-400 mb-6">Are you sure you want to delete this post? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostManagement;
